<script setup>
import AdminLayout from "@/Layouts/AdminLayout/AdminLayout.vue"
import { ref, computed } from "vue"
import { usePage, router } from "@inertiajs/vue3"

const props = defineProps({
    pairings: {
        type: Object,
        required: true
    },
    filters: {
        type: Object,
        default: () => ({})
    },
    stats: {
        type: Object,
        default: () => ({})
    }
})

const page = usePage()
const currencySymbol = computed(() => page.props.currencySymbol || '$')

// Filter states
const searchQuery = ref(props.filters.search || '')
const dateFrom = ref(props.filters.date_from || '')
const dateTo = ref(props.filters.date_to || '')
const weakerLeg = ref(props.filters.weaker_leg || '')
const showFilters = ref(false)

const formatCurrency = (value) => {
    if (value === null || value === undefined) return `${currencySymbol.value}0.00`
    const num = parseFloat(value) || 0
    return currencySymbol.value + new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(num)
}

const formatNumber = (value) => {
    if (value === null || value === undefined) return '0'
    return new Intl.NumberFormat('en-US').format(value)
}

const formatDate = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })
}

const formatDateShort = (dateString) => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    })
}

const getInitials = (name) => {
    if (!name || typeof name !== 'string') return '?'
    return name.split(' ').map(n => n.charAt(0)).join('').toUpperCase().slice(0, 2)
}

const applyFilters = () => {
    router.get('/admin/binary/pairings', {
        search: searchQuery.value,
        date_from: dateFrom.value,
        date_to: dateTo.value,
        weaker_leg: weakerLeg.value,
        page: 1
    }, {
        preserveState: true,
        preserveScroll: true
    })
}

const resetFilters = () => {
    searchQuery.value = ''
    dateFrom.value = ''
    dateTo.value = ''
    weakerLeg.value = ''
    router.get('/admin/binary/pairings', {}, {
        preserveState: true
    })
}

const exportPairings = () => {
    const params = new URLSearchParams({
        search: searchQuery.value || '',
        date_from: dateFrom.value || '',
        date_to: dateTo.value || '',
        weaker_leg: weakerLeg.value || ''
    })
    window.location.href = `/admin/binary/pairings/export?${params.toString()}`
}

const viewUserTree = (userId) => {
    router.get(`/admin/binary/tree?user_id=${userId}`)
}

const legDistributionPercent = computed(() => {
    const total = (props.stats.left_leg_pairings || 0) + (props.stats.right_leg_pairings || 0)
    if (total === 0) return { left: 50, right: 50 }
    return {
        left: Math.round((props.stats.left_leg_pairings / total) * 100),
        right: Math.round((props.stats.right_leg_pairings / total) * 100)
    }
})
</script>

<template>
    <AdminLayout title="Binary Pairings History">
        <div class="space-y-6">
            <!-- Header -->
            <div class="mb-6">
                <div class="flex items-center justify-between">
                    <div>
                        <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
                            Pairing History
                        </h1>
                        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
                            Complete record of all binary pairing transactions and rewards
                        </p>
                    </div>
                    <div class="flex items-center space-x-3">
                        <!-- Filter Toggle -->
                        <button
                            @click="showFilters = !showFilters"
                            class="px-4 py-2 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors flex items-center space-x-2 shadow-sm"
                        >
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"></path>
                            </svg>
                            <span class="font-medium">Filters</span>
                        </button>
                        
                        <!-- Export Button -->
                        <button
                            @click="exportPairings"
                            class="px-4 py-2 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors flex items-center space-x-2 shadow-sm"
                        >
                            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                            </svg>
                            <span class="font-medium">Export</span>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Stats Overview -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <!-- Total Pairings -->
                <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6 hover:shadow-md transition-shadow duration-200">
                    <div class="flex items-center justify-between mb-4">
                        <div class="p-3 bg-blue-500 rounded-lg">
                            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                        </div>
                    </div>
                    <h3 class="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                        {{ formatNumber(stats.total_pairings) }}
                    </h3>
                    <p class="text-sm text-gray-600 dark:text-gray-400">Total Pairings</p>
                </div>

                <!-- Total Rewards -->
                <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6 hover:shadow-md transition-shadow duration-200">
                    <div class="flex items-center justify-between mb-4">
                        <div class="p-3 bg-green-500 rounded-lg">
                            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                        </div>
                    </div>
                    <h3 class="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                        {{ formatCurrency(stats.total_rewards) }}
                    </h3>
                    <p class="text-sm text-gray-600 dark:text-gray-400">Total Rewards Paid</p>
                </div>

                <!-- Today's Stats -->
                <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6 hover:shadow-md transition-shadow duration-200">
                    <div class="flex items-center justify-between mb-4">
                        <div class="p-3 bg-purple-500 rounded-lg">
                            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                            </svg>
                        </div>
                    </div>
                    <h3 class="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                        {{ formatNumber(stats.today_pairings) }}
                    </h3>
                    <p class="text-sm text-gray-600 dark:text-gray-400">Pairings Today</p>
                </div>

                <!-- Average Reward -->
                <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6 hover:shadow-md transition-shadow duration-200">
                    <div class="flex items-center justify-between mb-4">
                        <div class="p-3 bg-orange-500 rounded-lg">
                            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path>
                            </svg>
                        </div>
                    </div>
                    <h3 class="text-3xl font-bold text-gray-900 dark:text-white mb-1">
                        {{ formatCurrency(stats.avg_reward) }}
                    </h3>
                    <p class="text-sm text-gray-600 dark:text-gray-400">Average Reward</p>
                </div>
            </div>

            <!-- Period Stats & Leg Distribution -->
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <!-- Period Statistics -->
                <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                    <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-4">Period Statistics</h3>
                    <div class="space-y-4">
                        <div class="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                            <div>
                                <p class="text-xs text-gray-500 dark:text-gray-400">This Week</p>
                                <p class="text-lg font-bold text-gray-900 dark:text-gray-100">{{ formatNumber(stats.weekly_pairings) }} pairings</p>
                            </div>
                            <div class="text-right">
                                <p class="text-sm font-bold text-green-600 dark:text-green-400">{{ formatCurrency(stats.weekly_rewards) }}</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400">rewards</p>
                            </div>
                        </div>
                        
                        <div class="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                            <div>
                                <p class="text-xs text-gray-500 dark:text-gray-400">This Month</p>
                                <p class="text-lg font-bold text-gray-900 dark:text-gray-100">{{ formatNumber(stats.monthly_pairings) }} pairings</p>
                            </div>
                            <div class="text-right">
                                <p class="text-sm font-bold text-green-600 dark:text-green-400">{{ formatCurrency(stats.monthly_rewards) }}</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400">rewards</p>
                            </div>
                        </div>
                        
                        <div class="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                            <div>
                                <p class="text-xs text-gray-500 dark:text-gray-400">Active Users (Week)</p>
                                <p class="text-lg font-bold text-gray-900 dark:text-gray-100">{{ formatNumber(stats.active_users_week) }} users</p>
                            </div>
                            <div class="text-right">
                                <p class="text-sm font-bold text-purple-600 dark:text-purple-400">{{ formatNumber(stats.active_users_today) }}</p>
                                <p class="text-xs text-gray-500 dark:text-gray-400">today</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Leg Distribution -->
                <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                    <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-4">Leg Distribution</h3>
                    <div class="space-y-4">
                        <div>
                            <div class="flex items-center justify-between mb-2">
                                <div class="flex items-center space-x-2">
                                    <div class="w-3 h-3 bg-blue-500 rounded-full"></div>
                                    <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Left Leg</span>
                                </div>
                                <span class="text-sm font-bold text-blue-600 dark:text-blue-400">{{ legDistributionPercent.left }}%</span>
                            </div>
                            <div class="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-3">
                                <div class="bg-blue-500 h-3 rounded-full transition-all duration-500" :style="`width: ${legDistributionPercent.left}%`"></div>
                            </div>
                            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ formatNumber(stats.left_leg_pairings) }} pairings</p>
                        </div>
                        
                        <div>
                            <div class="flex items-center justify-between mb-2">
                                <div class="flex items-center space-x-2">
                                    <div class="w-3 h-3 bg-purple-500 rounded-full"></div>
                                    <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Right Leg</span>
                                </div>
                                <span class="text-sm font-bold text-purple-600 dark:text-purple-400">{{ legDistributionPercent.right }}%</span>
                            </div>
                            <div class="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-3">
                                <div class="bg-purple-500 h-3 rounded-full transition-all duration-500" :style="`width: ${legDistributionPercent.right}%`"></div>
                            </div>
                            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ formatNumber(stats.right_leg_pairings) }} pairings</p>
                        </div>
                        
                        <div class="pt-4 border-t border-gray-200 dark:border-slate-700">
                            <div class="flex items-center justify-between text-sm">
                                <span class="text-gray-600 dark:text-gray-400">Total Distribution</span>
                                <span class="font-bold text-gray-900 dark:text-gray-100">{{ formatNumber(stats.left_leg_pairings + stats.right_leg_pairings) }} pairings</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Filters -->
            <transition
                enter-active-class="transition ease-out duration-200"
                enter-from-class="opacity-0 -translate-y-2"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition ease-in duration-150"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 -translate-y-2"
            >
                <div v-if="showFilters" class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6">
                    <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div class="md:col-span-1">
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Search User</label>
                            <input
                                v-model="searchQuery"
                                type="text"
                                placeholder="Name or email..."
                                class="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                @keyup.enter="applyFilters"
                            >
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date From</label>
                            <input
                                v-model="dateFrom"
                                type="date"
                                class="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date To</label>
                            <input
                                v-model="dateTo"
                                type="date"
                                class="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                        </div>
                        <div>
                            <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Weaker Leg</label>
                            <select
                                v-model="weakerLeg"
                                class="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">All Legs</option>
                                <option value="left">Left</option>
                                <option value="right">Right</option>
                            </select>
                        </div>
                    </div>
                    <div class="flex items-center justify-end space-x-3 mt-4">
                        <button
                            @click="resetFilters"
                            class="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg font-medium transition-colors"
                        >
                            Reset
                        </button>
                        <button
                            @click="applyFilters"
                            class="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition-colors"
                        >
                            Apply Filters
                        </button>
                    </div>
                </div>
            </transition>

            <!-- Pairings Table -->
            <div class="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 overflow-hidden">
                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
                        <thead class="bg-gray-50 dark:bg-slate-700">
                            <tr>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    User
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Volume
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Matched
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Weaker Leg
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Pairs
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Reward
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Date
                                </th>
                                <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Action
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
                            <tr v-if="pairings.data.length === 0">
                                <td colspan="8" class="px-6 py-12 text-center">
                                    <div class="flex flex-col items-center">
                                        <svg class="w-12 h-12 text-gray-400 dark:text-gray-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"></path>
                                        </svg>
                                        <p class="text-gray-500 dark:text-gray-400 font-medium">No pairings found</p>
                                        <p class="text-sm text-gray-400 dark:text-gray-500 mt-1">Try adjusting your filters</p>
                                    </div>
                                </td>
                            </tr>
                            <tr v-for="pairing in pairings.data" :key="pairing.id" class="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="flex items-center">
                                        <div class="flex-shrink-0 h-10 w-10 bg-blue-500 rounded-full flex items-center justify-center">
                                            <span class="text-sm font-semibold text-white">
                                                {{ getInitials(pairing.user_wallet_address || pairing.user_name) }}
                                            </span>
                                        </div>
                                        <div class="ml-4">
                                            <div class="text-sm font-medium text-gray-900 dark:text-white">{{ pairing.user_wallet_address || pairing.user_name }}</div>
                                            <div class="text-xs text-gray-500 dark:text-gray-400">{{ pairing.user_email }}</div>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-xs text-gray-500 dark:text-gray-400">
                                        <div class="flex items-center space-x-2 mb-1">
                                            <span class="text-blue-600 dark:text-blue-400 font-semibold">L:</span>
                                            <span>{{ formatNumber(pairing.left_volume) }}</span>
                                        </div>
                                        <div class="flex items-center space-x-2">
                                            <span class="text-purple-600 dark:text-purple-400 font-semibold">R:</span>
                                            <span>{{ formatNumber(pairing.right_volume) }}</span>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-sm font-semibold text-gray-900 dark:text-white">
                                        {{ formatNumber(pairing.matched_volume) }}
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span 
                                        class="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full"
                                        :class="pairing.weaker_leg === 'left' 
                                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400' 
                                            : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400'"
                                    >
                                        {{ pairing.weaker_leg }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-sm font-bold text-gray-900 dark:text-white">
                                        {{ pairing.pair_count }}
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="text-sm font-bold text-green-600 dark:text-green-400">
                                        {{ formatCurrency(pairing.reward_amount) }}
                                    </div>
                                    <div class="text-xs text-gray-500 dark:text-gray-400">
                                        {{ pairing.reward_percentage }}% rate
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {{ formatDateShort(pairing.pairing_date) }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <button
                                        @click="viewUserTree(pairing.user_id)"
                                        class="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                                    >
                                        View Tree
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Pagination -->
                <div v-if="pairings.links && pairings.links.length > 3" class="bg-gray-50 dark:bg-slate-700/50 px-4 py-3 border-t border-gray-200 dark:border-slate-700 sm:px-6">
                    <div class="flex items-center justify-between">
                        <div class="flex-1 flex justify-between sm:hidden">
                            <a
                                v-if="pairings.prev_page_url"
                                :href="pairings.prev_page_url"
                                class="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-slate-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700"
                            >
                                Previous
                            </a>
                            <a
                                v-if="pairings.next_page_url"
                                :href="pairings.next_page_url"
                                class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-slate-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700"
                            >
                                Next
                            </a>
                        </div>
                        <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p class="text-sm text-gray-700 dark:text-gray-300">
                                    Showing
                                    <span class="font-medium">{{ pairings.from }}</span>
                                    to
                                    <span class="font-medium">{{ pairings.to }}</span>
                                    of
                                    <span class="font-medium">{{ pairings.total }}</span>
                                    results
                                </p>
                            </div>
                            <div>
                                <nav class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                    <component
                                        v-for="(link, index) in pairings.links"
                                        :key="index"
                                        :is="link.url ? 'a' : 'span'"
                                        :href="link.url"
                                        v-html="link.label"
                                        class="relative inline-flex items-center px-4 py-2 border text-sm font-medium"
                                        :class="[
                                            link.active
                                                ? 'z-10 bg-blue-50 dark:bg-blue-900/30 border-blue-500 text-blue-600 dark:text-blue-400'
                                                : link.url
                                                    ? 'bg-white dark:bg-slate-800 border-gray-300 dark:border-slate-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-700'
                                                    : 'bg-gray-100 dark:bg-slate-700 border-gray-300 dark:border-slate-600 text-gray-400 dark:text-gray-500 cursor-not-allowed',
                                            index === 0 ? 'rounded-l-md' : '',
                                            index === pairings.links.length - 1 ? 'rounded-r-md' : ''
                                        ]"
                                    />
                                </nav>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    </AdminLayout>
</template>
